// Office sign-in: "Sign in with Google", restricted to @eastlondonelevators.co.uk, OR email + password
// (Director decision, 26 September 2026: office/admin accounts are no longer Google-only). The Google
// button is a full-page navigation to the server's OAuth start (/api/auth/google); the server redirects
// back here with ?error=... on failure, which we turn into a plain-English message. Engineers sign in on
// a separate screen (/engineer/login), though that same server route now also admits any role.
// Session ASB: a session the SERVER ended arrives as ?reason=... (ProtectedRoute), which is a
// notice rather than an error: "Signed out after inactivity", never a raw code.
import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ApiError, GOOGLE_SIGN_IN_URL } from "../lib/api";

// Codes the server's /api/auth/google/callback redirects back with.
const ERROR_MESSAGES: Record<string, string> = {
  domain: "Access restricted to East London Elevators accounts.",
  cancelled: "Sign-in was cancelled. Please try again.",
  state: "Your sign-in session expired. Please try again.",
  exchange: "Could not complete sign-in with Google. Please try again.",
  // Session AEO: the server knows the sign-in cannot complete from this web address and says
  // so up front, instead of sending the user to Google and back for a mystery failure.
  misconfigured: "Sign-in is not set up correctly for this web address. Please contact your administrator.",
  // Session ASB: Google sign-in no longer switches a deactivated account back on.
  deactivated: "This account has been deactivated. Ask an administrator to reactivate it.",
  // Session ASG (D120): a Google account with no platform account is refused, never created.
  no_account:
    "You do not have an account on this system yet. Please ask an administrator to create your account first, or call the office on 0203 576 4760.",
};

// Why the server ended the session (Session ASB). Anything else a reason could say reads the same as an ended one.
const REASON_MESSAGES: Record<string, string> = {
  idle_timeout: "Signed out after inactivity. Please sign in again.",
};
const SESSION_ENDED_MESSAGE = "Your session has ended. Please sign in again.";

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

export function Login() {
  const { engineerLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const error = params.get("error");
  const message = error ? (ERROR_MESSAGES[error] ?? "Could not sign in right now. Please try again.") : null;
  const reason = params.get("reason");
  const notice = !message && reason ? (REASON_MESSAGES[reason] ?? SESSION_ENDED_MESSAGE) : null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoginError(null);
    setBusy(true);
    try {
      // Same server route the engineer app uses: username OR email + password, any role.
      await engineerLogin(email.trim(), password);
      navigate("/office", { replace: true });
    } catch (err) {
      setLoginError(
        err instanceof ApiError && err.code === "invalid_credentials"
          ? "Those details did not match, or this account is no longer active."
          : "Could not sign in right now. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-ele-bg px-4 text-ele-body">
      <img
        src="/ELE_logo_new_reqt_FINALv2PNG.png"
        alt="East London Elevators"
        className="mb-6 block h-auto w-auto max-w-[200px]"
      />
      <div className="w-full max-w-sm rounded-ele border border-ele-purple-accent bg-black/20 p-8 text-center">
        <h1 className="mb-1 font-heading text-3xl tracking-wide">
          East London <span className="text-ele-green">Elevators</span>
        </h1>
        <p className="mb-6 font-menu text-xs uppercase tracking-widest text-ele-blue">Office sign-in</p>

        {message && (
          <p role="alert" aria-live="assertive" className="mb-4 rounded-ele border border-ele-danger bg-black/20 px-3 py-2 text-sm text-ele-white">
            {message}
          </p>
        )}
        {notice && (
          <p role="status" aria-live="polite" className="mb-4 rounded-ele border border-ele-purple-accent bg-black/20 px-3 py-2 text-sm text-ele-white">
            {notice}
          </p>
        )}

        <a
          href={GOOGLE_SIGN_IN_URL}
          className="flex w-full items-center justify-center gap-3 rounded-ele bg-white py-2.5 font-heading text-sm uppercase tracking-wide text-[#3c4043] shadow-sm transition hover:brightness-95"
        >
          <GoogleG />
          Sign in with Google
        </a>
        <p className="mt-3 break-words text-xs text-ele-body">Use your @eastlondonelevators.co.uk account.</p>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-ele-body">
          <span className="h-px flex-1 bg-ele-purple-accent" />
          or
          <span className="h-px flex-1 bg-ele-purple-accent" />
        </div>

        <form onSubmit={onSubmit} className="text-left">
          <label className="mb-1 block text-sm" htmlFor="office-email">
            Email
          </label>
          <input
            id="office-email"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mb-4 w-full rounded-ele border border-ele-purple-accent bg-black/20 px-3 py-2 outline-none focus:border-ele-green"
          />

          <label className="mb-1 block text-sm" htmlFor="office-password">
            Password
          </label>
          <input
            id="office-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mb-4 w-full rounded-ele border border-ele-purple-accent bg-black/20 px-3 py-2 outline-none focus:border-ele-green"
          />

          {loginError && (
            <p role="alert" aria-live="assertive" className="mb-4 rounded-ele border border-ele-danger bg-black/20 px-3 py-2 text-sm text-ele-white">
              {loginError}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || !email.trim() || !password}
            className="min-h-11 w-full rounded-ele bg-ele-green py-2 font-heading tracking-wide text-ele-bg transition disabled:opacity-50"
          >
            {busy ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <p className="mt-6 border-t border-ele-purple-accent pt-4 text-xs text-ele-body">
          Engineer?{" "}
          <Link to="/engineer/login" className="text-ele-green hover:underline">
            Sign in here
          </Link>
          .
        </p>
        <p className="mt-4 font-menu text-xs text-ele-body">Need help? Call the office on 0203 576 4760.</p>
      </div>
    </div>
  );
}
